import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type {
	AIDefinitionsPluginConfig,
	ReadingAidsPopupPropsInState,
	SelectionRect,
} from '../../types';

import { READING_AIDS_POPUP_PORTAL_CONTAINER } from './const';

const WHITESPACE_REGEX = /\s+/u;

const getSelectedText = (editorState: EditorState) => {
	const { from, to } = editorState.selection;
	const text = editorState.doc.textBetween(from, to, '\n');
	return text.trim();
};

export const selectionHasText = (editorState: EditorState): boolean => {
	if (editorState.selection.empty) {
		return false;
	}
	const text = getSelectedText(editorState);
	return text.length > 0;
};

const selectionWordCount = (editorState: EditorState) => {
	const text = getSelectedText(editorState);
	return text.split(WHITESPACE_REGEX).length;
};

const DEFAULT_MAX_WORD_COUNT = 1;

export const selectionExceedsMaxWordCount = (
	editorState: EditorState,
	config: AIDefinitionsPluginConfig,
): boolean => {
	const wordCount = selectionWordCount(editorState);
	return wordCount > (config.readingAidsMaxWordCount ?? DEFAULT_MAX_WORD_COUNT);
};

/**
 * Get the selection rect relative to the portal container, code references:
 * - handleAcronymClick in confluence/next/packages/contextual-reading-aids/src/RendererTextHighlighter.tsx
 * - selectionRect in confluence/next/packages/highlight-actions/src/HighlightActions.tsx
 * @param range
 * @example
 */
export function getSelectionRect(range: Range): {
	height: number;
	left: number;
	top: number;
	width: number;
} | null {
	const portalContainer = document.getElementById(READING_AIDS_POPUP_PORTAL_CONTAINER);
	if (!portalContainer) {
		return null;
	}

	const portalRect = portalContainer.getBoundingClientRect();
	const rect = range.getBoundingClientRect();
	const selectionRect = {
		top: rect.top - portalRect.top,
		left: rect.left - portalRect.left,
		width: rect.width,
		height: rect.height,
	};

	return selectionRect;
}

/**
 * Build a portal-relative `selectionRect` + `selectionRange` for a document
 * position range, so the ReadingAidsPopup can anchor to a node (e.g. a
 * smartlink) that is not represented by a text selection.
 *
 * Returns `null` values when the editorView has no DOM at the position or the
 * portal container is not mounted.
 */
export const getReadingAidsRectForPos = (
	editorView: EditorView,
	from: number,
	to: number,
): { selectionRange?: Range; selectionRect: SelectionRect | null } => {
	try {
		const fromDom = editorView.domAtPos(from);
		const toDom = editorView.domAtPos(to);

		if (!fromDom?.node || !toDom?.node) {
			return { selectionRect: null };
		}

		const range = document.createRange();
		range.setStart(fromDom.node, fromDom.offset);
		range.setEnd(toDom.node, toDom.offset);

		const selectionRect = getSelectionRect(range);
		return { selectionRange: range, selectionRect };
	} catch {
		return { selectionRect: null };
	}
};

export const getReadingAidsSelectionProps = (
	state?: EditorState,
): ReadingAidsPopupPropsInState | null => {
	if (!state || !state.selection || state.selection.empty) {
		return null;
	}
	const selection = window.getSelection();
	if (!selection || !selection.rangeCount) {
		return null;
	}

	const range = selection.getRangeAt(0);

	if (!range || range.collapsed) {
		return null;
	}
	const { from, to } = state.selection;
	const selectedText = state.doc.textBetween(from, to, '\n');
	const selectionRect = getSelectionRect(range);

	return {
		selectionRange: range,
		selectionRect,
		selectedText,
		// ReadingAidsPopup will apply a position shift to the popup.
		// - see file next/packages/contextual-reading-aids/src/ReadingAidsPopup.tsx
		isEditor: true,
	};
};
